import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiClientError, type ProductCardDto, type ProductDto } from '../lib/api';
import { useCart } from '../lib/cart';
import { useStore } from '../lib/store';
import { usePageMeta } from '../lib/meta';
import { discountPercent, price } from '../lib/format';
import { ProductGrid, ProductImage, SaleBadge } from '../components/ProductCard';
import { Alert, Button, EmptyState, Spinner } from '../components/ui';

function Gallery({ product }: { product: ProductDto }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = product.images[activeIndex];

  // Reset to the first photo when navigating between products, otherwise the
  // previous product's index carries over and can land out of range.
  useEffect(() => {
    setActiveIndex(0);
  }, [product.id]);

  return (
    <div className="flex flex-col-reverse gap-4 md:flex-row">
      {product.images.length > 1 && (
        <div
          className="flex gap-3 overflow-x-auto md:max-h-[640px] md:w-20 md:flex-col md:overflow-y-auto"
          role="tablist"
          aria-label="Product photographs"
        >
          {product.images.map((image, index) => (
            <button
              key={image.id}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              aria-label={`View photograph ${index + 1} of ${product.images.length}`}
              onClick={() => setActiveIndex(index)}
              className={`h-20 w-20 shrink-0 cursor-pointer overflow-hidden border transition-colors ${
                index === activeIndex ? 'border-ink' : 'border-transparent hover:border-border'
              }`}
            >
              <img
                src={image.src}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      <div className="relative flex-1 bg-surface">
        <ProductImage
          image={active}
          eager
          sizes="(min-width: 1024px) 50vw, 100vw"
          className="aspect-square w-full object-cover"
        />
        {product.compareAtMinor && (
          <SaleBadge percent={discountPercent(product.priceMinor, product.compareAtMinor)} />
        )}
      </div>
    </div>
  );
}

export function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: storeData } = useStore();
  const { add, loading: cartLoading, error: cartError, clearError } = useCart();

  const [product, setProduct] = useState<ProductDto | null>(null);
  const [categories, setCategories] = useState<Array<{ id: string; slug: string; title: string }>>([]);
  const [related, setRelated] = useState<ProductCardDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [justAdded, setJustAdded] = useState(false);

  usePageMeta({
    title: product
      ? `${product.seo.title || product.title} | ${storeData?.store?.name ?? "Gill's Handmade"}`
      : 'Loading…',
    description: product?.seo.description,
    image: product?.images[0]?.src,
  });

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    setLoading(true);
    setNotFound(false);
    setJustAdded(false);
    window.scrollTo({ top: 0 });

    void api
      .product(slug)
      .then((response) => {
        if (cancelled) return;
        setProduct(response.product);
        setCategories(response.categories);
        setRelated(response.related);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiClientError && error.status === 404) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="page-container flex justify-center py-32">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <EmptyState
        title="We could not find that piece"
        message="It may have sold, or the link may be out of date."
        action={
          <Link
            to="/collections/all-jewellery"
            className="text-sm underline underline-offset-4"
          >
            Browse all jewellery
          </Link>
        }
      />
    );
  }

  const currency = product.currency as 'GBP';

  return (
    <div className="page-container py-8">
      <nav className="mb-6 text-xs text-muted" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-ink">
          Home
        </Link>
        <span className="mx-2">/</span>
        <Link to="/collections/all-jewellery" className="hover:text-ink">
          Jewellery
        </Link>
        <span className="mx-2">/</span>
        <span className="text-ink">{product.title.split('|')[0]?.trim()}</span>
      </nav>

      <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
        <Gallery product={product} />

        <div className="lg:sticky lg:top-32 lg:self-start">
          <h1 className="text-2xl leading-snug md:text-3xl">
            {product.title.split('|')[0]?.trim() ?? product.title}
          </h1>

          {/* The imported titles pack extra descriptors after a pipe; showing
              them smaller keeps the search keywords without shouting. */}
          {product.title.includes('|') && (
            <p className="mt-2 text-sm text-muted">
              {product.title.split('|').slice(1).join(' · ').trim()}
            </p>
          )}

          <div className="mt-5 flex items-baseline gap-3">
            <span
              className={`text-2xl ${product.compareAtMinor ? 'text-sale' : 'text-ink'}`}
            >
              {price(product.priceMinor, currency)}
            </span>
            {product.compareAtMinor && (
              <>
                <span className="text-base text-muted line-through">
                  {price(product.compareAtMinor, currency)}
                </span>
                <span className="bg-sale px-2 py-0.5 text-[10px] uppercase tracking-widest text-white">
                  Save {discountPercent(product.priceMinor, product.compareAtMinor)}%
                </span>
              </>
            )}
          </div>

          {product.saleTitle && (
            <p className="mt-2 text-xs uppercase tracking-widest text-sale">
              {product.saleTitle}
            </p>
          )}

          <div className="mt-6 border-y border-border py-4">
            {product.inStock ? (
              <p className="flex items-center gap-2 text-sm">
                <span className="inline-block h-2 w-2 rounded-full bg-success" />
                {product.stock === 1
                  ? 'One of a kind — only one available'
                  : `In stock (${product.stock} available)`}
              </p>
            ) : (
              <p className="flex items-center gap-2 text-sm text-muted">
                <span className="inline-block h-2 w-2 rounded-full bg-muted" />
                Sold — this piece has found a home
              </p>
            )}
          </div>

          {cartError && (
            <div className="mt-4">
              <Alert onDismiss={clearError}>{cartError}</Alert>
            </div>
          )}

          <div className="mt-6 space-y-3">
            <Button
              size="lg"
              className="w-full"
              disabled={!product.inStock}
              loading={cartLoading}
              onClick={() => {
                void add(product.id).then((ok) => {
                  if (ok) {
                    setJustAdded(true);
                    window.setTimeout(() => setJustAdded(false), 2500);
                  }
                });
              }}
            >
              {product.inStock ? 'Add to basket' : 'Sold out'}
            </Button>

            {justAdded && (
              <p className="text-center text-xs text-success" role="status">
                Added to your basket
              </p>
            )}
          </div>

          <div className="mt-6 space-y-1.5 text-xs text-muted">
            <p>Free UK delivery · Royal Mail Tracked 24</p>
            <p>Dispatched within 3 working days</p>
            <p>Sent in a linen pouch or recycled kraft gift box</p>
          </div>

          {product.descriptionHtml && (
            <div
              className="rich-text mt-8 text-sm text-ink"
              // The description comes from Gillian's own Shopify content, not
              // from shoppers, so this is first-party HTML rather than
              // untrusted input.
              dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
            />
          )}

          {categories.length > 0 && (
            <div className="mt-8 border-t border-border pt-6">
              <h2 className="mb-3 text-xs uppercase tracking-widest text-muted">
                Also in
              </h2>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/collections/${category.slug}`}
                    className="border border-border px-3 py-1.5 text-xs transition-colors hover:border-ink"
                  >
                    {category.title}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-24">
          <h2 className="mb-8 border-b border-border pb-4 text-2xl">You may also like</h2>
          <ProductGrid products={related} eagerCount={0} />
        </section>
      )}
    </div>
  );
}
